import { configs } from '@configs'
import { asyncWithLDProvider, useFlags } from 'launchdarkly-react-client-sdk'
// TODO: Re-enable when DevUI branch is merged
// import { useDevUi } from '@utils/DevUIPanel/DevUiProvider'
import React from 'react'
import { log as loglevel } from '@utils/Logger'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'

export const useLaunchDarkly = () => {
  type LDProviderType = React.ComponentType<{ children: React.ReactNode }>
  const [LDProvider, setLDProvider] = React.useState<LDProviderType | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  // Use ref to prevent double initialization
  const isInitializing = React.useRef(false)
  const hasInitialized = React.useRef(false)

  // Create a fallback provider that just renders children without any LD context
  const FallbackProvider: LDProviderType = ({ children }) => children as JSX.Element

  const initializeLaunchDarkly = React.useCallback(async () => {
    // Prevent double initialization
    if (isInitializing.current || hasInitialized.current) {
      return
    }

    isInitializing.current = true
    setIsLoading(true)
    setError(null)

    try {
      const getCurrentBrand = (): string | undefined => {
        try {
          const stored = DataStore.getItem<string>(EMkStorageKeys.MK_THEME_BRAND)
          return stored || (configs.mk?.defaultThemeBrand as any) || configs.mk?.brands?.[0]
        } catch {
          return (configs.mk?.defaultThemeBrand as any) || configs.mk?.brands?.[0]
        }
      }
      const clientId = configs.mk.launchDarklyClientId
      if (!clientId) {
        // Use the top-level fallback provider
        setLDProvider(() => FallbackProvider)
        setError('LaunchDarkly client ID is not configured. Feature flags will use default values.')
        console.warn('LaunchDarkly client ID is not configured. Feature flags will use default values.')
        return
      }

      // Detect currently logged-in username from cookie (if available)
      const getCookie = (name: string) => {
        if (typeof document === 'undefined') return null
        const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'))
        return match ? decodeURIComponent(match[1]) : null
      }
      const rawUsername = getCookie('user_name')
      const username = rawUsername && typeof rawUsername === 'string' ? rawUsername.trim() : null
      const currentBrand = getCurrentBrand()

      const provider = await asyncWithLDProvider({
        clientSideID: String(clientId),
        // Provide an explicit initial context so LD can evaluate targeted rules (brand, user, anonymous, etc.)
        context: (username
          ? {
              kind: 'user',
              key: username,
              anonymous: false,
              username,
              brand: currentBrand,
              app: 'mkfront',
            }
          : {
              kind: 'user',
              key: 'anonymous',
              anonymous: true,
              brand: currentBrand,
              app: 'mkfront',
            }) as any,
        // Initialize immediately with provided context so user-targeted rules (e.g., key == "sirLoin") evaluate right away
        // Note: Previously set to deferInitialization: true which could delay evaluations unexpectedly
        // deferInitialization: true,
        options: {
          streaming: false,
          fetchGoals: false,
          sendEvents: false,
          logger: {
            // Only emit LD internal logs when loglevel is set to TRACE
            debug: (...args: any[]) => {
              if (loglevel.getLevel() <= loglevel.levels.TRACE) console.debug('[LaunchDarkly]', ...args)
            },
            info: (...args: any[]) => {
              if (loglevel.getLevel() <= loglevel.levels.TRACE) console.info('[LaunchDarkly]', ...args)
            },
            warn: (...args: any[]) => {
              if (loglevel.getLevel() <= loglevel.levels.TRACE) console.warn('[LaunchDarkly]', ...args)
            },
            error: (...args: any[]) => console.error('[LaunchDarkly]', ...args),
          },
        },
      })

      setLDProvider(() => provider as LDProviderType)
      hasInitialized.current = true
    } catch (err) {
      // Use the already defined fallback provider
      setLDProvider(() => FallbackProvider)

      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to initialize LaunchDarkly: ${errorMessage}. Feature flags will use default values.`)
      console.error('Failed to initialize LaunchDarkly:', err)
    } finally {
      setIsLoading(false)
      isInitializing.current = false
    }
  }, [])

  React.useEffect(() => {
    // Only initialize if not already done
    if (!hasInitialized.current && !isInitializing.current) {
      initializeLaunchDarkly()
    }
  }, [initializeLaunchDarkly]) // This ensures the callback runs only once

  return {
    LDProvider,
    error,
    isLoading,
    retry: initializeLaunchDarkly,
  }
}

// TODO: Re-enable when DevUI branch is merged
// Hook to consume flags with Dev Panel overrides applied when enabled
export const useLDFlagsWithDevOverrides = <T extends Record<string, any> = Record<string, any>>() => {
  let rawFlags: T
  try {
    rawFlags = useFlags() as T
  } catch (_) {
    // No LD provider available; return empty flags
    rawFlags = {} as T
  }
  // const dev = useDevUi()
  // if (!dev.enabled) return rawFlags
  // const overrides = dev.flagOverrides || {}
  // // Shallow merge of boolean overrides only
  // const merged = { ...rawFlags }
  // Object.keys(overrides).forEach((k) => {
  //   const v = overrides[k]
  //   if (typeof v === 'boolean') (merged as any)[k] = v
  // })
  // return merged
  return rawFlags
}

// Helper: list current flags for UI
export const useLDFlagKeys = () => {
  let flags: Record<string, any> = {}
  try {
    flags = useFlags() as Record<string, any>
  } catch (_) {
    flags = {}
  }
  const keys = React.useMemo(() => Object.keys(flags || {}).sort(), [JSON.stringify(flags || {})])
  return keys
}
